import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { User, KeyRound, LogOut, CheckCircle, Trash2 } from 'lucide-react';
import {
  isAccountLoggedIn,
  getAccessToken,
  getAccountEmail,
  changeAccountPassword,
  accountSignOut,
  deleteAccount,
} from '../accountAuth';
import { deleteMyCode, getMyAccount } from '../api/accountApi';
import { Button, Card, Input, Alert } from './ui';

const AccountSettings = () => {
  const navigate = useNavigate();
  const [tokenChecked, setTokenChecked] = useState(false);
  const email = getAccountEmail();

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAccountLoggedIn()) {
      navigate('/account/login');
      return;
    }
    getAccessToken().then((token) => {
      if (!token) navigate('/account/login');
      setTokenChecked(true);
    });
  }, []);

  if (!tokenChecked) return null;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(false);
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    setPwLoading(true);
    try {
      await changeAccountPassword(currentPassword, newPassword);
      setPwSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Password change failed';
      setPwError(msg.includes('Incorrect') || msg.includes('incorrect') ? 'Current password is incorrect.' : msg);
    } finally {
      setPwLoading(false);
    }
  };

  const handleSignOut = () => {
    accountSignOut();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const account = await getMyAccount();
      for (const code of account.codes) await deleteMyCode(code.handle);
      await deleteAccount();
      navigate('/');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Account deletion failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Settings - YouWont</title>
      </Helmet>
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-8">Settings</h1>

          {/* Account Info */}
          <Card className="brand-panel mb-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center shrink-0">
                <User size={18} className="text-brand dark:text-blue-400" />
              </div>
              <div>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{email}</p>
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate('/my-codes')}>
              View My Codes
            </Button>
          </Card>

          {/* Change Password */}
          <Card className="brand-panel mb-5">
            <div className="flex items-center gap-2 mb-5">
              <KeyRound size={18} className="text-slate-500 dark:text-slate-400" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Change Password</h2>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <Input
                label="Current Password"
                type="password"
                placeholder="Your current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <Input
                label="New Password"
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                hint="Min 8 characters with uppercase, lowercase, number, and symbol"
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />

              {pwError && <Alert variant="danger">{pwError}</Alert>}
              {pwSuccess && (
                <Alert variant="success">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} />
                    Password updated successfully.
                  </div>
                </Alert>
              )}

              <Button type="submit" variant="primary" disabled={pwLoading} className="w-full">
                {pwLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </Card>

          {/* Sign Out */}
          <Card className="brand-panel">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">Sign Out</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">You can always come back. We won't guilt-trip you.</p>
              </div>
              <Button variant="secondary" onClick={handleSignOut}>
                <LogOut size={15} className="mr-1.5" />
                Sign Out
              </Button>
            </div>
          </Card>

          <Card className="brand-panel mt-5 border-red-200 dark:border-red-900/50">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={18} className="text-red-500" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Delete Account</h2>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Permanently deletes your account, codes, messages, and history. This cannot be undone.
            </p>
            <Input
              label="Type DELETE to confirm"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoComplete="off"
            />
            {deleteError && <Alert variant="danger" className="mt-3">{deleteError}</Alert>}
            <Button
              variant="danger"
              className="w-full mt-4"
              disabled={deleteConfirmation !== 'DELETE' || deleteLoading}
              onClick={handleDeleteAccount}
            >
              {deleteLoading ? 'Deleting account...' : 'Permanently Delete Account'}
            </Button>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AccountSettings;
